import type { SxProps, Theme } from "@mui/material/styles";
import { colors } from "../../../../../utils/colors";

export type DoubleDonutLegendPosition = "top" | "right";

export const genericDoubleDonutDefaultCategoryColors = [
  "#1565C0",
  "#2E7D32",
  "#4527A0",
  "#E65100",
  "#00695C",
];

export const genericDoubleDonutCategorySourcePalettes = [
  ["#0D47A1", "#1565C0", "#1976D2", "#1E88E5", "#0277BD", "#00838F"],
  ["#388E3C", "#F9A825", "#EF6C00", "#D81B60", "#7B1FA2", "#455A64"],
  ["#4527A0", "#283593", "#00695C", "#4E342E", "#37474F", "#1B5E20"],
];

export const genericDoubleDonutChartConfig = {
  holeRadius: 26,
  ringThickness: 21,
  ringGap: 2,
  defaultHeight: 400,
  defaultMinHeight: 400,
  emptyStateMinHeight: "17.5rem",
  legendTop: 8,
  legendItemSize: 12,
  legendItemGap: 12,
  labelLineLength2: 10,
  labelDistanceToLine: 4,
  emphasisScaleSize: 6,
  sliceBorderWidth: 2,
  innerLabelLineMinLength: 8,
  innerLabelLinePadding: 8,
  resizeDebounceMs: 350,
  tooltipZIndex: 10000,
};

const { holeRadius, ringThickness, ringGap } = genericDoubleDonutChartConfig;

export const genericDoubleDonutDefaultInnerRadius: [string, string] = [
  `${holeRadius}%`,
  `${holeRadius + ringThickness}%`,
];

export const genericDoubleDonutDefaultOuterRadius: [string, string] = [
  `${holeRadius + ringThickness + ringGap}%`,
  `${holeRadius + 2 * ringThickness + ringGap}%`,
];

export const genericDoubleDonutPieCenterByLegend: Record<
  DoubleDonutLegendPosition,
  { x: string; y: string }
> = {
  top: { x: "50%", y: "58%" },
  right: { x: "50%", y: "50%" },
};

export const genericDoubleDonutInnerLabelStyle = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  overflow: "none" as const,
};

export const genericDoubleDonutTooltipTextStyle = {
  color: colors.chart_text,
  fontSize: "0.75rem",
  fontWeight: 400,
};

export const genericDoubleDonutLegendTextStyle = {
  color: colors.chart_text,
  fontSize: "0.75rem",
};

export const genericDoubleDonutSliceItemStyle = {
  borderColor: colors.white,
  borderWidth: genericDoubleDonutChartConfig.sliceBorderWidth,
};

export const genericDoubleDonutEmphasisStyle = {
  scale: true,
  scaleSize: genericDoubleDonutChartConfig.emphasisScaleSize,
};

export const getGenericDoubleDonutCategoryColor = (
  index: number,
  explicitColor?: string,
) =>
  explicitColor ??
  genericDoubleDonutDefaultCategoryColors[
    index % genericDoubleDonutDefaultCategoryColors.length
  ];

export const getGenericDoubleDonutSourceColors = (
  categoryIndex: number,
  sourceCount: number,
) => {
  const palette =
    genericDoubleDonutCategorySourcePalettes[
      categoryIndex % genericDoubleDonutCategorySourcePalettes.length
    ];
  if (palette.length > 0) {
    return Array.from(
      { length: sourceCount },
      (_, index) => palette[index % palette.length],
    );
  }
};

export const formatGenericDoubleDonutNameValueLabel = (
  name: string,
  value: number,
  percentage: number,
) =>
  `${name}\n${value.toLocaleString("en-US")} (${percentage.toFixed(2)}%)`;

const getInnerLabelLineLength = (chartHeight: number) => {
  const half = chartHeight / 2;
  const innerRingOuterPx = half * ((holeRadius + ringThickness) / 100);
  const outerRingOuterPx =
    half * ((holeRadius + 2 * ringThickness + ringGap) / 100);
  const { innerLabelLineMinLength, innerLabelLinePadding } =
    genericDoubleDonutChartConfig;
  return Math.max(
    innerLabelLineMinLength,
    Math.round(outerRingOuterPx - innerRingOuterPx + innerLabelLinePadding),
  );
};

type InnerSeriesDatum = {
  name: string;
  value: number;
  percentage: number;
  itemStyle: { color: string };
};

const buildInnerLabelLine = (chartHeight: number, lineColor: string) => ({
  show: true,
  length: getInnerLabelLineLength(chartHeight),
  length2: genericDoubleDonutChartConfig.labelLineLength2,
  smooth: false,
  lineStyle: {
    color: lineColor,
    width: 1,
  },
});

export const buildGenericDoubleDonutInnerSeriesData = (
  data: InnerSeriesDatum[],
  chartHeight: number,
) =>
  data.map((item) => {
    const sliceColor = item.itemStyle.color;
    return {
      ...item,
      label: {
        color: sliceColor,
        ...genericDoubleDonutInnerLabelStyle,
      },
      labelLine: buildInnerLabelLine(chartHeight, sliceColor),
    };
  });

export const getGenericDoubleDonutTitleSx = (
  titleAlign: "left" | "center" | "right",
): SxProps<Theme> => ({
  px: 2,
  mb: "0.25rem",
  fontWeight: 500,
  fontSize: "1rem",
  color: colors.grayish_blue,
  textAlign: titleAlign,
});

export const genericDoubleDonutChartStyles = {
  root: {
    minWidth: 0,
  },
  emptyStateContainer: {
    minWidth: 0,
    px: 2,
    py: 6,
    minHeight: genericDoubleDonutChartConfig.emptyStateMinHeight,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  chartWrapper: (minHeight: number, loading: boolean, hasData: boolean) => ({
    minHeight,
    px: 2,
    py: 1,
    opacity: loading && !hasData ? 0.5 : 1,
    minWidth: 0,
    overflow: "visible",
  }),
  chartLayout: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    width: "100%",
    minWidth: 0,
  },
  chartArea: (showRightLegend: boolean, height: number) => ({
    position: "relative",
    flex: showRightLegend ? "1 1 58%" : "1 1 100%",
    minWidth: 0,
    height,
    overflow: "visible",
  }),
  chartCanvas: {
    width: "100%",
    minWidth: 0,
    height: "100%",
    overflow: "visible",
  },
  chartPlaceholder: (minHeight: number) => ({
    width: "100%",
    minHeight,
    minWidth: 0,
  }),
  centerLabelWrapper: (pieCenterX: string, pieCenterY: string) => ({
    position: "absolute",
    left: pieCenterX,
    top: pieCenterY,
    transform: "translate(-50%, -50%)",
    pointerEvents: "none",
    textAlign: "center",
  }),
  centerLabelTitle: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: colors.grayish_blue,
    lineHeight: 1.3,
  },
  centerLabelValue: {
    fontSize: "1rem",
    fontWeight: 600,
    color: colors.grayish_blue,
    lineHeight: 1.3,
  },
};

export const genericDoubleDonutRightLegendStyles = {
  root: {
    py: 1,
    minWidth: 0,
    flex: "0 1 42%",
  },
  categoryTitle: (categoryColor: string) => ({
    color: categoryColor,
    fontWeight: 600,
    fontSize: "0.875rem",
    lineHeight: 1.4,
    mb: 0.75,
  }),
  sourceRow: {
    minWidth: 0,
  },
  sourceSwatch: (sourceColor: string) => ({
    width: "0.8125rem",
    height: "0.8125rem",
    borderRadius: "0.125rem",
    bgcolor: sourceColor,
    flexShrink: 0,
  }),
  sourceLabel: {
    fontSize: "0.75rem",
    color: colors.chart_text,
    lineHeight: 1.4,
    minWidth: 0,
  },
};

export const getGenericDoubleDonutLegendConfig = (showRightLegend: boolean) =>
  showRightLegend
    ? { show: false }
    : {
        type: "scroll" as const,
        orient: "horizontal" as const,
        top: genericDoubleDonutChartConfig.legendTop,
        left: "center" as const,
        textStyle: genericDoubleDonutLegendTextStyle,
        icon: "roundRect" as const,
        itemWidth: genericDoubleDonutChartConfig.legendItemSize,
        itemHeight: genericDoubleDonutChartConfig.legendItemSize,
        itemGap: genericDoubleDonutChartConfig.legendItemGap,
      };

export const genericDoubleDonutInnerSeriesLabelConfig = {
  show: true,
  position: "outside" as const,
  alignTo: "none" as const,
  distanceToLabelLine: genericDoubleDonutChartConfig.labelDistanceToLine,
};
